import * as Joi from 'joi'
import { ja } from '../language'

const v = Joi.defaults(schema => {
  return schema.prefs({
    messages: ja, // conditional render - lang en or jp
    abortEarly: true,
    presence: 'required',
  })
})

const password = v
  .string()
  .regex(/^[a-zA-Z0-9]{6,30}$/)
  .label('password')
const email = v
  .string()
  .regex(
    /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
  )
  .message({
    en: 'is invalid email.',
    ja: 'は無効なメールアドレスです。',
  })
const phone = v.string().regex(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)
const colorCode = v.string().regex(/^#([\da-fA-F]{6}|[\da-fA-F]{3})$/)
const accountId = v
  .string()
  .regex(/^[a-z0-9_]+$/)
  .max(20)
  .label('アカウントID')
const introduction = v.string().max(200).allow('').label('自己紹介')
const avatarPath = v.string().allow('').optional().label('プロフィール画像')
const displayName = v.string().max(50).label('表示名')
const price = v.number().max(100000).min(100).allow(0).label('価格')

const createSubscription = {
  amount: v.number(),
  content: v.string(),
  artistId: v.string(),
}

const updateEmail = {
  email,
  password,
}

const updatePassword = {
  oldPassword: password.label('現在のパスワード'),
  newPassword: password
    .label('新しいパスワード')
    .valid(v.ref('confirmPassword'))
    .messages({ any: { allowOnly: '!!確認用パスワードと異なります。' } }),
  confirmPassword: password.label('新しいパスワード(確認)'),
}

const updateCreditCard = {
  name: v.string().label('名前'),
  number: v
    .string()
    .length(16)
    .regex(/^[0-9]+$/)
    .label('カード番号'),
  cvc: v
    .string()
    .length(3)
    .regex(/^[0-9]+$/)
    .label('セキュリティコード'),
  expYear: v
    .string()
    .max(2)
    .regex(/^[0-9]+$/)
    .label('有効期限(年)'),
  expMonth: v
    .string()
    .max(2)
    .regex(/^[0-9]+$/)
    .label('有効期限(月)'),
}

const createItem = {
  type: v.string().valid('video', 'audio', 'magazine').label('カードタイプ'),
  title: v.string().max(100).label('カード名'),
  price,
  description: v.string().max(200).label('カード説明'),
  cardImagePath: v.string().label('カード画像'),
  isPremium: v.boolean().default(false).label('メンバー限定'),
  issuedNumber: v.number().max(100000).allow(null).label('発行数'),
  color: colorCode.optional().allow('').default('#000').label('テーマカラー'),
  resourceId: v
    .string()
    .label('コンテンツ')
    .when('type', {
      is: v.valid('ticket'),
      then: v.allow('').optional(),
    }),
  copyrights: v.array().items(v.string()).max(10).label('申請楽曲'),
  thumbnailType: v.valid('square').allow(null).label('サムネイルタイプ'),
}

const updateProfile = {
  displayName: displayName.optional(),
  introduction: introduction.optional(),
  accountId: accountId.optional(),
  avatarPath: avatarPath.optional(),
}

const updateItem = {
  id: v.string(),
  price,
}

const signUpWithEmail = {
  email,
  password,
}

const birthday = v.date().allow(null).label('生年月日')
const gender = v.valid('male', 'female').allow(null).label('性別')

const registerProfile = {
  accountId,
  displayName,
  introduction,
  avatarPath,
  gender,
  birthday,
}

const registerArtist = {
  displayName,
  accountId,
  email,
}

const signupAsArtist = {
  email,
  displayName,
  accountId,
  password,
  entry: v.string().optional().allow(''),
}

const resetPassword = {
  email,
}

const updateTwitterInfo = {
  displayName,
  introduction,
  accountId,
  avatarPath,
}

const updateBank = {
  bankCode: v
    .string()
    .length(4)
    .pattern(/^[0-9]+$/)
    .label('銀行コード'),
  branchCode: v
    .string()
    .length(3)
    .pattern(/^[0-9]+$/)
    .label('支店コード'),
  accountNumber: v
    .string()
    .length(7)
    .pattern(/^[0-9]+$/)
    .label('口座番号'),
  accountHolderName: v
    .string()
    .max(20)
    .pattern(/^[ァ-ヴー\-\s]+$/)
    .label('口座名義（カナ）'),
}

const updateBusiness = {
  phone: v
    .string()
    .pattern(/^[0-9]+$/)
    .label('電話番号'),
  birthday: v.date().label('生年月日'),
  gender: v.valid('male', 'female').label('性別'),
  firstNameKanji: v
    .string()
    .pattern(/^[一-龥]+$/)
    .label('姓（漢字）'),
  lastNameKanji: v
    .string()
    .pattern(/^[一-龥]+$/)
    .label('名（漢字）'),
  firstNameKana: v
    .string()
    .pattern(/^[ぁ-ん]+$/)
    .label('姓（かな）'),
  lastNameKana: v
    .string()
    .pattern(/^[ぁ-ん]+$/)
    .label('名（かな）'),
  address: {
    postalCode: v
      .string()
      .pattern(/^[0-9]+$/)
      .label('郵便番号'),
    state: v.string().max(47).label('都道府県'),
    town: v.string().label('市'),
    city: v.string().label('町'),
    stateKana: v.string().label('都道府県（カナ）'),
    townKana: v.string().label('市（カナ）'),
    cityKana: v.string().label('町（カナ）'),
    line1: v.string().allow('').label('番地'),
    line2: v.string().allow('').label('建物・部屋番号'),
    line1Kana: v
      .string()
      .allow('')
      .pattern(/^[0-9０-９ァ-ヴー\-\s]+$/)
      .label('番地（カナ）'),
    line2Kana: v
      .string()
      .allow('')
      .pattern(/^[0-9０-９ァ-ヴー\-\s]+$/)
      .label('建物・部屋番号（カナ）'),
  },
}

const createCollection = {
  title: v.string().max(100).label('ホルダー名'),
  description: v.string().max(200).label('説明'),
  thumbnailPath: v.string().label('サムネイル'),
}

const editCollection = {
  title: v.string().max(100).label('ホルダー名'),
  description: v.string().max(200).label('説明'),
  thumbnailPath: v.string().label('サムネイル').optional(),
  id: v.string().label('ホルダーID'),
}

const createPost = {
  resourceType: v
    .valid('image', 'text', 'video', 'sound')
    .messages({ 'any.only': 'ポストに添付するコンテンツの種類が未定です。' }),
  resource: v.object().when('resourceType', [
    { is: 'text', then: v.any().valid({}) },
    {
      is: 'image',
      then: v.object({
        tmpPath: v
          .string()
          .messages({ 'any.required': '画像の添付が必須です。' }),
      }),
    },
    {
      is: 'video',
      then: v.object({
        videoId: v.string().messages({
          'any.required': '有効な動画または音声ファイルのURLが必須です。',
        }),
      }),
    },
    {
      is: 'sound',
      then: v.object({
        uri: v.string().messages({
          'any.required': '有効な動画または音声ファイルのURLが必須です。',
        }),
      }),
    },
  ]),
  content: v.string().label('本文').max(1000),
  isPremium: v.boolean(),
  copyrights: v.array().items(v.string()).optional(),
}

const createGacha = {
  name: v.string().max(100).label('タイトル'),
  artistId: v.string().max(100).label('アーティストID'),
  description: v.string().max(200).label('概要'),
  thumbnailPath: v.string().max(200).label('サムネイル'),
  rollAnimationPath: v.string().max(200).label('アニメーション効果'),
}

const updateGachaItemAndRarity = {
  itemRaritySets: v.array().items(
    v.object({
      weight: v.number().max(100).label('確率'),
      name: v.string().max(100).label('名前'),
      itemId: v.string().max(100).label('登場アイテム'),
    })
  ),
  // items: v.array().items(v.string()).label('登場アイテム'),
}

const answerQuizMission = {
  answer: v
    .string()
    .max(Joi.ref('$limitTextNumber', { render: true }))
    .label('回答'),
}

const adminCreateMission = {
  reward1: v.any().optional(),
  reward2: v.any().optional(),
  artistId: v.string().label('アーティストID'),
  type: v.string().label('ミッションタイプ'),
  title: v.string().label('タイトル'),
  description: v.string().label('説明'),
  contentImagePath: v.string().label('画像'),
  contentAnswer: v.string().label('解答'),
  isPublished: v.string().label('公開フラグ'),
  expiredAt: v.date().label('有効期限'),
  rewardItemId: v.when('reward1', {
    is: 'itemId',
    then: v.string().label('アイテムID'),
    otherwise: v.any().optional(),
  }),
  rewardGachaTicketId: v.when('reward2', {
    is: 'gachaTicket',
    then: v.string().label('ガチャチケットID'),
    otherwise: v.any().optional(),
  }),
  rewardGachaTicketCount: v.when('reward2', {
    is: 'gachaTicket',
    then: v.number().integer().label('ガチャチケット枚数'),
    otherwise: v.any().optional(),
  }),
}

const adminGiveGachaTicket = {
  gachaTicketId: v.string().label('ガチャチケットID'),
  userId: v.string().label('ユーザーID'),
  count: v.number().integer().min(1).max(20).label('枚数'),
  reason: v.string().allow('').max(200).optional().label('理由'),
}

export {
  updateEmail,
  updatePassword,
  updateCreditCard,
  createItem,
  updateItem,
  updateProfile,
  registerProfile,
  registerArtist,
  signUpWithEmail,
  resetPassword,
  createSubscription,
  updateTwitterInfo,
  signupAsArtist,
  password,
  updateBank,
  updateBusiness,
  createCollection,
  editCollection,
  createPost,
  createGacha,
  updateGachaItemAndRarity,
  answerQuizMission,
  adminCreateMission,
  adminGiveGachaTicket,
}
